import * as styles from "./EmployeeField.css";

import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { type JSX, type ReactNode, useEffect, useMemo } from "react";

import { Combobox as BaseCombobox } from "@base-ui/react/combobox";
import {
  Combobox,
  ComboboxContent,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxInput,
  ComboboxItem,
} from "@frontend/components/atoms/Combobox";
import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import type { NumberField } from "@frontend/lib/form";
import { useLingui } from "@lingui/react/macro";

import { employeesAtom, loadEmployeesAtom } from "../stores";
import type { EmployeeOption } from "../types";
import { EmployeeAvatar } from "./EmployeeAvatar";

type EmployeeGroup = {
  value: string;
  items: EmployeeOption[];
};

export type EmployeeFieldProps = {
  field: NumberField<number | null | undefined>;
  /** What the field is called, when the form names the person otherwise. */
  label?: ReactNode;
  /** What to call the employee when the roster no longer offers them. */
  fallbackLabel?: string | null;
  disabled?: boolean;
  error?: string | null;
  onEmployeeSelected?: (employeeId: number | undefined) => void;
};

/**
 * Names someone from the roster. Four hundred people is more than a select
 * shows well, so the combobox filters them in the browser and files them under
 * their department, the way the roster itself is read.
 */
export function EmployeeField({
  field,
  label,
  fallbackLabel,
  disabled,
  error,
  onEmployeeSelected,
}: EmployeeFieldProps): JSX.Element {
  const { t } = useLingui();

  const [employeeId, setEmployeeId] = useAtom(field.atom);

  const employees = useAtomValue(employeesAtom);
  const loadEmployees = useSetAtom(loadEmployeesAtom);

  useEffect(() => {
    void loadEmployees();
  }, [loadEmployees]);

  const groups = useMemo(
    () => byDepartment(employees, t`Without department`),
    [employees, t],
  );

  const listed = employees.find((employee) => employee.id === employeeId);

  // An archived employee is still named on the records that named them, and
  // the roster no longer holds them: they read, and are not offered again.
  const selected =
    listed ??
    (null == employeeId
      ? null
      : {
          id: employeeId,
          label: fallbackLabel ?? String(employeeId),
          department: null,
          available: false,
          avatarUrl: null,
          hasPhoto: false,
        });

  return (
    <Field invalid={Boolean(error)}>
      <FieldLabel>{label ?? t`Employee`}</FieldLabel>

      <FieldContent>
        <Combobox
          items={groups}
          value={selected}
          onValueChange={(employee: EmployeeOption | null) => {
            const nextEmployeeId = employee?.id ?? undefined;

            setEmployeeId(nextEmployeeId);
            onEmployeeSelected?.(nextEmployeeId);
          }}
          itemToStringLabel={(employee: EmployeeOption | null) =>
            employee?.label ?? ""
          }
          isItemEqualToValue={(a: EmployeeOption, b: EmployeeOption) =>
            a.id === b.id
          }
          disabled={disabled}
        >
          <ComboboxInput
            placeholder={t`Choose an employee`}
            leftSection={
              selected ? (
                <EmployeeAvatar employee={selected} size="auto" />
              ) : null
            }
          />

          <ComboboxContent empty={t`Nobody matches this search.`}>
            {(group: EmployeeGroup) => (
              <ComboboxGroup key={group.value} items={group.items}>
                <ComboboxGroupLabel>{group.value}</ComboboxGroupLabel>

                <BaseCombobox.Collection>
                  {(employee: EmployeeOption) => (
                    <ComboboxItem key={employee.id} value={employee}>
                      <span className={styles.item}>
                        <EmployeeAvatar
                          employee={employee}
                          size="auto"
                          className={styles.itemAvatar}
                        />

                        <span className={styles.itemLabel}>
                          {employee.label}
                        </span>
                      </span>
                    </ComboboxItem>
                  )}
                </BaseCombobox.Collection>
              </ComboboxGroup>
            )}
          </ComboboxContent>
        </Combobox>

        <FieldError match={Boolean(error)}>{error}</FieldError>
      </FieldContent>
    </Field>
  );
}

/** Departments in alphabetical order, the people who have none last. */
function byDepartment(
  employees: EmployeeOption[],
  orphanLabel: string,
): EmployeeGroup[] {
  const named = new Map<string, EmployeeOption[]>();
  const orphans: EmployeeOption[] = [];

  for (const employee of employees) {
    if (null === employee.department || "" === employee.department) {
      orphans.push(employee);

      continue;
    }

    const group = named.get(employee.department);

    if (group) {
      group.push(employee);

      continue;
    }

    named.set(employee.department, [employee]);
  }

  const groups = [...named.entries()]
    .map(([value, items]) => ({ value, items }))
    .sort((a, b) => a.value.localeCompare(b.value));

  return 0 === orphans.length
    ? groups
    : [...groups, { value: orphanLabel, items: orphans }];
}
